/* Send-a-friend sample pack gift flow.
   Shared by snippets/cart-drawer.liquid and sections/main-cart-items.liquid -
   those two files each render their own copy of the offer/form/added-state
   markup (no shared line-item snippet exists between them), sharing only
   this script and assets/component-sample-pack-gift.css.

   Deliberately does NOT touch assets/cart.js or assets/cart-drawer.js -
   this posts to /cart/add.js and /cart/change.js directly and swaps its
   own sections, the same way the existing CartItems class does, without
   depending on or modifying its CartItems/CartDrawer/CartRemoveButton
   classes. Guarded against double-definition (this file is loaded
   unconditionally from both surfaces, matching quantity-popover.js's own
   pattern rather than cart.js's cart_type-conditional guard) - there are
   no customElements here, so nothing to guard against re-declaring; event
   delegation on `document` means re-running this file twice is harmless
   (duplicate listeners on the same delegated events would double-fire, so
   this file guards itself with a single top-level `if` instead).

   Both cart surfaces render this feature's markup inside a real <form>
   element (CartDrawer-Form / #cart) - any block-level element added here
   needs checking for illegal nesting inside that form, or inside any
   other element this markup sits within. This has bitten twice already:
   the form wrapper itself couldn't be a <form> (nesting forms), and the
   two-line offer copy couldn't be a <p> wrapping <p> children. Both times
   the browser didn't error - it silently dropped the invalid tag and
   hoisted its children into the parent instead, so the bug only showed
   up as "the element I expected just isn't in the DOM," not a console
   warning. Confirm nesting validity by hand before adding new markup.

   Line item property keys, stored via buildProperties() below - the
   exact strings written to the cart and read back everywhere (this file,
   both Liquid files, admin's order/cart display):
     "Friend name"
     "Friend address"
   (There used to be a third, "Note" - removed along with the form's note
   field: the sender's name comes from the order and there's no custom
   message, so nothing to capture. An order placed before this change can
   still carry a Note property on its line item - the cart page's generic,
   key-agnostic property loop renders whatever properties exist with no
   per-key special-casing, so it just keeps showing that line for such an
   order rather than erroring or needing an explicit skip.)
   These are deliberately apostrophe-free, even though the visible labels
   shown to shoppers still read "Friend's name" and "Delivery address".
   Liquid returns item.properties keys pre-escaped for HTML, so a key
   containing an apostrophe comes back as the literal string
   "Friend&#39;s name" wherever Liquid reads it (cart-drawer.liquid,
   main-cart-items.liquid) - but the raw /cart.js JSON this file itself
   reads does NOT escape keys. That mismatch broke the Edit-prefill flow
   the first time round and is easy to reintroduce: renaming any of these
   three keys back to an apostrophe form will silently break Liquid-side
   matching again without touching this file. Don't rename them without
   also fixing every matching literal in both Liquid files.

   The drawer's form overlay is deliberately scoped to .drawer__inner
   with position: absolute, clipped by its existing overflow: hidden,
   rather than using the sitewide <modal-dialog> element. modal-dialog's
   connectedCallback relocates to document.body and its overlay is
   position: fixed to the viewport, which would double-dim the page
   behind the drawer and also dim the drawer panel itself. The focus-trap
   utilities from global.js (trapFocus/removeTrapFocus) are reused - just
   not the element. (There used to be a second, separate overlay of this
   same shape - a terms/info popup, on both surfaces - reached via the
   "We'll send them 3 x 3mL sachets" line. Both surfaces' popups are gone
   now: that line converges on the same form/overlay Add opens, and its
   copy renders inline at the bottom of the form instead, via the shared
   'sample-pack-gift-terms-copy' snippet.)

   "FREE" in line 1 needs font-family: var(--font-heading-family)
   alongside font-weight: 700, not weight alone. The offer row's body
   text uses --font-body-family, which maps to NeuePlak Regular and has
   no bold face loaded, so a weight bump on that family alone produces a
   synthetic bold rather than the real Extra Black face.

   Escape handling for the drawer's form overlay is a capture-phase
   listener on keyup, not keydown, because the drawer's own close
   listener in cart-drawer.js listens on keyup. Stopping keydown does
   nothing to a separate keyup event. Anyone changing this needs to
   match the event the drawer actually listens for.

   cart-drawer-items (component-cart-drawer.css) sets scroll-snap-type:
   y mandatory, with scroll-snap-align: start only on .cart-item rows.
   Anything rendered after the last .cart-item - .sample-pack-gift and
   .cart-drawer__subscription-note both do - needs that same
   scroll-snap-align itself, or scrolling to it snaps straight back to
   the last .cart-item's own snap point instead, and the content becomes
   unreachable even though it's genuinely in the DOM and nothing looks
   wrong in a static inspection. This is exactly what made the old
   inline expanded form unreachable before it became its own overlay
   below - confirmed live by setting scrollTop directly and watching it
   revert. Whoever adds the next thing to the bottom of this drawer's
   item list needs the same scroll-snap-align, or this resurfaces. */

if (!window.__samplePackGiftInitialized) {
  window.__samplePackGiftInitialized = true;

  var SAMPLE_PACK_VARIANT_ID = 56225429487908;

  var SURFACE_CONTAINER_SELECTOR = {
    drawer: '#CartDrawer-CartItems [data-sample-pack-gift]',
    page: '#main-cart-items [data-sample-pack-gift]',
  };

  /* The expanded form's overlay, drawer-only - see the Liquid comment
     next to its markup (snippets/cart-drawer.liquid) and the CSS comment
     next to .sample-pack-gift__form-overlay for why. No 'page' entry:
     the cart page keeps its old inline form, toggled via [hidden] on
     the form itself rather than a separate overlay. */
  var FORM_OVERLAY_ID = {
    drawer: 'SamplePackFormOverlay-Drawer',
  };

  /* Resolves to the drawer's form overlay when container lives inside
     the drawer's item list, or null for the page surface (which has no
     overlay - its form stays inline, toggled via [hidden] as before). */
  function getFormOverlay(container) {
    if (container && container.closest('#CartDrawer-CartItems')) {
      return document.getElementById(FORM_OVERLAY_ID.drawer);
    }
    return null;
  }

  /* The form lives inside the overlay for the drawer (a sibling of
     container, not a descendant - .closest() from something inside the
     form can't reach container directly, see handleSubmit below for how
     that's resolved instead), or inside container itself for the page,
     exactly as before. */
  function getFormEl(container) {
    var overlay = getFormOverlay(container);
    return overlay ? qs(overlay, '[data-sample-pack-form]') : qs(container, '[data-sample-pack-form]');
  }

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }

  function trim(value) {
    return (value || '').replace(/^\s+|\s+$/g, '');
  }

  /* Section ids requested together so one request updates both surfaces.
     'cart-drawer' and 'cart-icon-bubble' are static ids (matching the
     literal strings assets/cart-drawer.js already hardcodes for the same
     sections). main-cart-items/main-cart-footer are NOT static - their
     real section id is whatever templates/cart.json names them (currently
     "cart-items"/"cart-footer") - read live from the same data-id
     attributes assets/cart.js already relies on, so this keeps working if
     that template's section keys ever change. Only included when present,
     since the cart page's DOM only exists on /cart - the drawer is
     site-wide and always present. */
  function getSectionIds() {
    var ids = { drawer: 'cart-drawer', icon: 'cart-icon-bubble' };
    var pageItems = document.getElementById('main-cart-items');
    var pageFooter = document.getElementById('main-cart-footer');
    if (pageItems && pageItems.dataset.id) ids.pageItems = pageItems.dataset.id;
    if (pageFooter && pageFooter.dataset.id) ids.pageFooter = pageFooter.dataset.id;
    return ids;
  }

  function sectionsParam(ids) {
    var list = [ids.drawer, ids.icon];
    if (ids.pageItems) list.push(ids.pageItems);
    if (ids.pageFooter) list.push(ids.pageFooter);
    return list;
  }

  function replaceFromSections(sectionsHtml, ids) {
    if (!sectionsHtml) return;

    var drawerTarget = document.getElementById('CartDrawer');
    if (drawerTarget && sectionsHtml[ids.drawer]) {
      var drawerDoc = new DOMParser().parseFromString(sectionsHtml[ids.drawer], 'text/html');
      var drawerSource = drawerDoc.getElementById('CartDrawer');
      if (drawerSource) drawerTarget.innerHTML = drawerSource.innerHTML;
    }

    var iconTarget = document.getElementById('cart-icon-bubble');
    if (iconTarget && sectionsHtml[ids.icon]) {
      var iconDoc = new DOMParser().parseFromString(sectionsHtml[ids.icon], 'text/html');
      var iconSource = iconDoc.querySelector('.shopify-section');
      if (iconSource) iconTarget.innerHTML = iconSource.innerHTML;
    }

    if (ids.pageItems && sectionsHtml[ids.pageItems]) {
      var pageItemsRoot = document.getElementById('main-cart-items');
      var pageItemsTarget = qs(pageItemsRoot, '.js-contents');
      if (pageItemsTarget) {
        var itemsDoc = new DOMParser().parseFromString(sectionsHtml[ids.pageItems], 'text/html');
        var itemsSource = itemsDoc.querySelector('.js-contents');
        if (itemsSource) pageItemsTarget.innerHTML = itemsSource.innerHTML;
      }
    }

    if (ids.pageFooter && sectionsHtml[ids.pageFooter]) {
      var pageFooterRoot = document.getElementById('main-cart-footer');
      var pageFooterTarget = qs(pageFooterRoot, '.js-contents');
      if (pageFooterTarget) {
        var footerDoc = new DOMParser().parseFromString(sectionsHtml[ids.pageFooter], 'text/html');
        var footerSource = footerDoc.querySelector('.js-contents');
        if (footerSource) pageFooterTarget.innerHTML = footerSource.innerHTML;
      }
    }
  }

  /* source: 'cart-items' is the exact string assets/cart.js's own
     CartItems.updateQuantity already publishes. Reusing it means:
     - FreeShippingBar and the header cart icon (assets/free-shipping-bar.js,
       assets/cart-drawer.js) update immediately - neither checks `source`,
       both just read `cartData` off the event.
     - CartItems/CartDrawerItems' own cartUpdate listener (assets/cart.js)
       explicitly skips its follow-up section re-fetch when
       event.source === 'cart-items' - since this file already replaced
       both surfaces' sections directly above, that skip avoids two
       redundant extra requests rather than causing anything to go stale. */
  function notifyCartUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cartData });
    }
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json();
    });
  }

  function setBusy(container, busy) {
    if (!container) return;
    container.classList.toggle('sample-pack-gift--busy', !!busy);
  }

  function clearFieldError(form, field) {
    var el = qs(form, '[data-sample-pack-error="' + field + '"]');
    if (el) el.textContent = '';
  }

  function clearErrors(form) {
    clearFieldError(form, 'name');
    clearFieldError(form, 'address');
  }

  function validate(form) {
    clearErrors(form);
    var nameInput = qs(form, '[data-sample-pack-name]');
    var addressInput = qs(form, '[data-sample-pack-address]');
    var firstInvalid = null;

    if (!trim(nameInput.value)) {
      qs(form, '[data-sample-pack-error="name"]').textContent = "Enter your friend's name";
      firstInvalid = firstInvalid || nameInput;
    }
    if (!trim(addressInput.value)) {
      qs(form, '[data-sample-pack-error="address"]').textContent = 'Enter a delivery address';
      firstInvalid = firstInvalid || addressInput;
    }

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }
    return true;
  }

  /* Keys are "Friend name"/"Friend address", not "Friend's name"/
     "Friend's address" - apostrophe-free on purpose. item.properties
     comes back from Liquid pre-escaped for HTML (the apostrophe becomes
     the literal string &#39;), which /cart.js's raw JSON does not do -
     confirmed live, and it silently broke the Edit prefill the first
     time round. Dropping the apostrophe from the key entirely removes
     the trap rather than papering over it with an escaped literal
     someone has to understand later. Visible labels are unaffected -
     they still read "Friend's name" / "Delivery address". */
  function buildProperties(form) {
    var props = {};
    props['Friend name'] = trim(qs(form, '[data-sample-pack-name]').value);
    props['Friend address'] = trim(qs(form, '[data-sample-pack-address]').value);
    return props;
  }

  /* The offer row (icon + both copy lines + trigger) is never hidden by
     this flow any more - only the form below it toggles. In 'add' mode
     the row was already visible (Liquid only hides it when the sample
     pack is already in the cart, sp_show_offer false) and stays that
     way through expand/collapse, per the mockup: icon and copy don't
     move, only the trigger's own content swaps (Add label <-> close
     icon, via aria-expanded in CSS). In 'edit' mode the row is already
     hidden by that same Liquid condition and this never touches it -
     nothing here needs to know which mode it's in for that part. */
  function openForm(container, prefill, mode, line, opener) {
    if (!container) return;
    var overlay = getFormOverlay(container);
    var form = getFormEl(container);
    if (!form) return;

    form.dataset.mode = mode;
    form.dataset.line = line || '';

    var nameInput = qs(form, '[data-sample-pack-name]');
    var addressInput = qs(form, '[data-sample-pack-address]');

    nameInput.value = (prefill && prefill.name) || '';
    addressInput.value = (prefill && prefill.address) || '';
    clearErrors(form);

    var trigger = qs(container, '[data-sample-pack-add-trigger]');

    /* Skipped below 750px - auto-focusing immediately raises the
       keyboard, which on a real device covers the submit button and
       scrolls the block's own heading out of view before the shopper
       has done anything. Letting them tap into a field themselves keeps
       that under their control; above 750px there's no keyboard to
       fight, so autofocus is still the better experience. Unchanged by
       the overlay move - still gated the same way, still untested at
       390px with a keyboard open (see the panel's own overflow:auto,
       added specifically so that untested case degrades to a scrollable
       panel rather than a repeat of the old trap either way). */
    if (overlay) {
      overlay.hidden = false;
      var panel = qs(overlay, '.sample-pack-gift__form-panel');
      var focusTarget = window.matchMedia('(min-width: 750px)').matches ? nameInput : panel || overlay;
      /* trapFocus() itself calls elementToFocus.focus() - this replaces
         the manual nameInput.focus() call used below for the page
         surface, rather than duplicating it before trapping focus. */
      trapFocus(overlay, focusTarget);
    } else {
      form.hidden = false;
      if (window.matchMedia('(min-width: 750px)').matches) {
        nameInput.focus();
      }
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
      /* The button's accessible name is normally its own visible "Add"
         text; once expanded that text is CSS-hidden in favour of the
         close icon (aria-hidden, no text of its own), so an explicit
         label is the only thing giving the button a name at that point.
         Still correct even though this trigger sits visually behind the
         drawer's overlay once open (trapFocus keeps it out of the tab
         order anyway) - a screen reader user landing on it by other
         means still hears the right state. */
      trigger.setAttribute('aria-label', 'Close');
    }

    /* Two controls (Add, and the "3 x 3mL sachets" line) can both open
       this same region now - returnFocus goes to whichever one was
       actually used (the caller's opener), not always the Add trigger,
       so closing lands focus back where the shopper's attention already
       was. Falls back to Add's own trigger if no opener was passed
       (nothing currently omits it in 'add' mode, but this keeps the
       function safe for any future caller that does). */
    container._samplePackReturnFocus =
      mode === 'edit'
        ? document.querySelector('[data-sample-pack-edit][data-line="' + line + '"]')
        : opener || trigger;
  }

  function closeForm(container) {
    if (!container) return;
    var overlay = getFormOverlay(container);
    var form = getFormEl(container);
    if (!form) return;

    var trigger = qs(container, '[data-sample-pack-add-trigger]');
    var returnFocus = container._samplePackReturnFocus || trigger;

    if (overlay) {
      overlay.hidden = true;
      removeTrapFocus(returnFocus && document.contains(returnFocus) ? returnFocus : null);
    } else {
      form.hidden = true;
      if (returnFocus && document.contains(returnFocus)) returnFocus.focus();
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-label');
    }

    container._samplePackReturnFocus = null;
  }

  function submitCart(url, body, container) {
    setBusy(container, true);
    return postJson(url, body)
      .then(function (parsedState) {
        if (parsedState && parsedState.status) {
          throw new Error((parsedState.errors && parsedState.errors.toString()) || 'cart-error');
        }
        replaceFromSections(parsedState.sections, getSectionIds());
        notifyCartUpdate(parsedState);
        return parsedState;
      })
      .finally(function () {
        setBusy(container, false);
      });
  }

  /* The form is a plain <div>, not a <form> element - both surfaces
     already wrap the whole item list (including this block) in their
     own real <form> (CartDrawer-Form / #cart, for checkout submission),
     and HTML doesn't allow forms to nest: a <form> tag encountered while
     already inside one is dropped by the parser, silently hoisting its
     children into the outer form instead (confirmed live - the intended
     element never existed in the DOM at all). So submission is handled
     here explicitly - a click on the submit button, or Enter pressed in
     the (single-line) name field - rather than a native submit event. */
  function handleSubmit(form) {
    if (!validate(form)) return;

    /* The drawer's form now lives inside the form overlay, a sibling of
       [data-sample-pack-gift] rather than a descendant of it - .closest()
       can't reach it from here any more, so resolve via the overlay's
       own data-surface the same way the edit-trigger flow already does. */
    var overlayAncestor = form.closest('[data-sample-pack-form-overlay]');
    var container = overlayAncestor
      ? document.querySelector(SURFACE_CONTAINER_SELECTOR[overlayAncestor.dataset.surface] || '')
      : form.closest('[data-sample-pack-gift]');
    var properties = buildProperties(form);
    var ids = getSectionIds();
    var body = {
      quantity: 1,
      properties: properties,
      sections: sectionsParam(ids),
      sections_url: window.location.pathname,
    };

    var request;
    if (form.dataset.mode === 'edit') {
      body.line = parseInt(form.dataset.line, 10);
      request = submitCart(routes.cart_change_url, body, container);
    } else {
      body.id = SAMPLE_PACK_VARIANT_ID;
      request = submitCart(routes.cart_add_url, body, container);
    }

    request
      .then(function () {
        closeForm(container);
      })
      .catch(function () {
        qs(form, '[data-sample-pack-error="address"]').textContent = 'Something went wrong - please try again.';
      });
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    var nameInput = event.target.closest('[data-sample-pack-name]');
    if (!nameInput) return;
    event.preventDefault();
    handleSubmit(nameInput.closest('[data-sample-pack-form]'));
  });

  /* Capture phase, on 'keyup' specifically (not 'keydown') - the drawer's
     own Escape-to-close (assets/cart-drawer.js, CartDrawer's constructor:
     `this.addEventListener('keyup', (evt) => evt.code === 'Escape' &&
     this.close())`) listens on 'keyup', attached directly on the
     <cart-drawer> element itself, bubble phase. A keydown listener can
     never stop that - keydown and keyup are separate events, and
     stopPropagation on one has no effect on the other, so the previous
     version of this fix (stopping keydown) still let the drawer's own
     keyup listener fire and close the drawer on the same Escape press.
     Capture phase on 'keyup' at the document level runs before <cart-
     drawer>'s own bubble-phase listener ever sees the event at all, so
     stopPropagation here genuinely prevents it from reaching the drawer -
     not just from reacting after the fact. Checked before closing (not
     after), so a second, separate Escape press - once nothing here is
     open - correctly falls through to close the drawer itself: layers
     unwind one at a time instead of both closing on one keypress. */
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape') return;
      /* stopPropagation here keeps this from ever reaching <cart-
         drawer>'s own bubble-phase Escape-to-close, so a second, separate
         Escape press - once this no longer matches - correctly falls
         through and closes the drawer itself: one layer unwinds at a
         time instead of both closing on one keypress. */
      var openFormOverlay = document.querySelector('[data-sample-pack-form-overlay]:not([hidden])');
      if (openFormOverlay) {
        event.stopPropagation();
        var container = document.querySelector(SURFACE_CONTAINER_SELECTOR[openFormOverlay.dataset.surface] || '');
        closeForm(container);
      }
    },
    true
  );

  document.addEventListener('click', function (event) {
    /* Both surfaces' controls converge on the same destination now: the
       "3 x 3mL sachets" line opens the exact same form Add opens (the
       drawer's overlay, or the page's inline form) - there's no longer a
       separate terms/info popup on either surface for this to branch on.
       No aria-expanded added here, unlike Add: this control's own
       appearance never changes between closed and open, so there's no
       state of its own to report - aria-expanded stays owned solely by
       Add, the one control whose visible state (label <-> close icon)
       actually toggles. No aria-haspopup either any more, for the same
       reason Add itself never had one: on the page this opens a plain
       inline region, not a dialog, so "haspopup" would be actively wrong
       there - and having it on one of two controls that do the identical
       thing but not the other would be an inconsistent signal for the
       same effective action. aria-controls points at the same form
       Add's aria-controls does, for the surface it's rendered on. */
    var line2Trigger = event.target.closest('[data-sample-pack-line2-trigger]');
    if (line2Trigger) {
      var line2Container = line2Trigger.closest('[data-sample-pack-gift]');
      openForm(line2Container, null, 'add', null, line2Trigger);
      return;
    }

    var formClose = event.target.closest('[data-sample-pack-form-close]');
    if (formClose) {
      var formOverlay = formClose.closest('[data-sample-pack-form-overlay]');
      var formOverlayContainer = formOverlay
        ? document.querySelector(SURFACE_CONTAINER_SELECTOR[formOverlay.dataset.surface] || '')
        : null;
      closeForm(formOverlayContainer);
      return;
    }

    var submitTrigger = event.target.closest('[data-sample-pack-submit]');
    if (submitTrigger) {
      handleSubmit(submitTrigger.closest('[data-sample-pack-form]'));
      return;
    }

    /* Same button opens and closes now - it doubles as the mockup's
       close-X once expanded, replacing the separate Cancel button that
       used to exist for that. */
    var addTrigger = event.target.closest('[data-sample-pack-add-trigger]');
    if (addTrigger) {
      var addContainer = addTrigger.closest('[data-sample-pack-gift]');
      if (addTrigger.getAttribute('aria-expanded') === 'true') {
        closeForm(addContainer);
      } else {
        openForm(addContainer, null, 'add', null, addTrigger);
      }
      return;
    }

    var editTrigger = event.target.closest('[data-sample-pack-edit]');
    if (editTrigger) {
      event.preventDefault();
      var surface = editTrigger.dataset.surface;
      var container = document.querySelector(SURFACE_CONTAINER_SELECTOR[surface] || '');
      if (!container) return;
      openForm(
        container,
        {
          name: editTrigger.dataset.name || '',
          address: editTrigger.dataset.address || '',
        },
        'edit',
        editTrigger.dataset.line
      );
      container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    var removeTrigger = event.target.closest('[data-sample-pack-remove]');
    if (removeTrigger) {
      event.preventDefault();
      var ids = getSectionIds();
      submitCart(
        routes.cart_change_url,
        {
          line: parseInt(removeTrigger.dataset.line, 10),
          quantity: 0,
          sections: sectionsParam(ids),
          sections_url: window.location.pathname,
        },
        null
      );
    }
  });

  /* Capture phase, so this runs before assets/cart.js's own bubble-phase
     'change' listener on the ancestor <cart-items>/<cart-drawer-items>
     element. Both this block's inputs and the theme's real quantity
     inputs share that ancestor - without this, blurring a sample-pack
     field (a native 'change' event) reached CartItems.onChange, which
     tried to resolve a #Quantity-<index> element for a target that has
     no data-index, threw (confirmed live via the console), and had
     nothing to do with the sample pack fields at all. */
  document.addEventListener(
    'change',
    function (event) {
      /* The drawer's fields now live inside the form overlay, a sibling
         of [data-sample-pack-gift] rather than a descendant of it, so
         that selector alone no longer catches them - checking both
         keeps this guard covering the page surface (still descendant-
         based) and the drawer surface (now overlay-based). */
      if (event.target.closest('[data-sample-pack-gift], [data-sample-pack-form-overlay]')) {
        event.stopPropagation();
      }
    },
    true
  );

  document.addEventListener('input', function (event) {
    var nameInput = event.target.closest('[data-sample-pack-name]');
    if (nameInput) {
      clearFieldError(nameInput.closest('[data-sample-pack-form]'), 'name');
      return;
    }

    var addressInput = event.target.closest('[data-sample-pack-address]');
    if (addressInput) {
      clearFieldError(addressInput.closest('[data-sample-pack-form]'), 'address');
    }
  });
}
